// external
import React, { useEffect } from 'react';
import { useLocation } from '@reach/router';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import Recaptcha from 'react-google-recaptcha';
import Cookies from 'js-cookie';
// shipae
import Checkbox from '@shipae/components-sandbox/component/checkbox';
import { ecoGreen } from '@shipae/components-sandbox/component/colors';
import FormElement from '@shipae/components-sandbox/component/form-element';
import Input from '@shipae/components-sandbox/component/input';
import PrimaryButton
  from '@shipae/components-sandbox/component/buttons/primary';
import ButtonTheme
  from '@shipae/components-sandbox/component/buttons/primary/theme/shipa-ecommerce'; // eslint-disable-line max-len
import FormElementTheme
  from '@shipae/components-sandbox/component/form-element/theme/shipa-ecommerce'; // eslint-disable-line max-len
import InputTheme
  from '@shipae/components-sandbox/component/input/theme/shipa-ecommerce'; // eslint-disable-line max-len
// global
import config from 'src/config';
import { loadComponent } from 'src/application/core/control/actions';
import { useTranslation } from 'src/views/misc/localization';
import { parseQueryString } from 'src/views/utils/parse-query-string';
import {
  resetForm,
  setRequiredEmail,
  setRequiredInput,
  // setCheckbox,
  submitForm,
  setCampaign,
  SetCampaign,
} from 'src/application/lazy/sec-extra-short/actions';
import {
  getSecExtraShortIsModuleLoaded,
  getSecExtraShortData,
} from 'src/application/lazy/sec-extra-short/selectors';
// local
import {
  FlexChild,
  Form,
  Container,
  RecaptchaError,
  GeneralError,
} from '../shared.styles';
import { getCampaign } from './service';

const SECExtraShortForm: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    isSuccess,
    isLoading,
    name,
    email,
    agreeToTermsAndConditions,
    recaptcha,
    generalError,
  } = useSelector(getSecExtraShortData);
  const isModuleLoaded = useSelector(getSecExtraShortIsModuleLoaded);

  useEffect((): () => void => {
    dispatch(loadComponent('sec-extra-short'));

    return () => dispatch(resetForm);
  }, []);

  useEffect(() => {
    if (!location || !isModuleLoaded) {
      return;
    }

    const query = parseQueryString(location.search);

    const latestTrafficSource: SetCampaign['payload'] = {
      name: query.utm_campaign,
      source: query.utm_source,
      medium: query.utm_medium,
    };

    const {
      name: CampaignName,
      source,
      medium,
    } = getCampaign(
      latestTrafficSource,
      Cookies.get('initialTrafficSource'),
    );

    dispatch(setCampaign({
      name: CampaignName,
      source,
      medium,
    }));
  }, [isModuleLoaded]);

  useEffect(() => {
    if (isSuccess) {
      if (typeof document !== 'undefined') {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }
    }
  }, [isSuccess]);

  if (isSuccess) {
    return null;
  }

  return (
    <Form onSubmit={(e) => {
      e.preventDefault();

      dispatch(submitForm);
    }}
    >
      <FlexChild width="50%" hasRightPadding>
        <FormElement
          theme={FormElementTheme}
          label={t('name')}
          hasValue={!!name.value}
          error={t(name.error)}
          disabled={isLoading}
          required
        >
          <Input
            theme={InputTheme}
            type="text"
            value={name.value}
            error={!!name.error}
            onChange={
                (newVal) => dispatch(
                  setRequiredInput('name')(newVal),
                )
                }
          />
        </FormElement>
      </FlexChild>
      <FlexChild width="100%">
        {/* instead of hasRightPadding, using calc */}
        <Container width="calc(50% - 4rem)">
          <FormElement
            theme={FormElementTheme}
            label={t('email')}
            hasValue={!!email.value}
            error={t(email.error)}
            disabled={isLoading}
            required
          >
            <Input
              theme={InputTheme}
              type="email"
              value={email.value}
              error={!!email.error}
              onChange={(newVal) => dispatch(
                setRequiredEmail('email')(newVal),
              )}
            />
          </FormElement>
        </Container>
      </FlexChild>
      <FlexChild>
        <FormElement
          theme={FormElementTheme}
          hasValue={agreeToTermsAndConditions.value.agreed}
          error={t(agreeToTermsAndConditions.error)}
          disabled={isLoading}
          data-test="sec-extra-short-form-agreement"
          required
        >
          <Checkbox
            checked={agreeToTermsAndConditions.value.agreed}
            label={t('SECExtraShortForm.agreeToTerms')}
            onChange={(
              // val: boolean,
            ) => {
              /* Disabled temporarily since we were not sure
               * about terms and conditions for shipa
              dispatch(
                setCheckbox(
                  'agreeToTermsAndConditions',
                  'agreed',
                )(!!val),
              );
              */
            }}
            color={ecoGreen()}
          />
        </FormElement>
      </FlexChild>
      <FlexChild width="100%" marginBottom="4rem">
        <Recaptcha
          sitekey={
            process.env.GATSBY_RECAPTCHA_CLIENT_KEY
            || config.defaultRecaptchaSiteKey
          }
          onChange={(response) => dispatch(
            setRequiredInput('recaptcha')(response || ''),
          )}
        />
        <RecaptchaError visible={!!recaptcha.error}>
          {t(recaptcha.error)}
        </RecaptchaError>
      </FlexChild>
      {generalError.message ? (
        <FlexChild width="100%" data-test="sec-get-in-touch-general-error">
          <GeneralError>
            {generalError.message}
          </GeneralError>
        </FlexChild>
      ) : null}
      <FlexChild width="100%">
        <PrimaryButton
          type="submit"
          loading={isLoading}
          theme={ButtonTheme}
        >
          {t('callMeBack')}
        </PrimaryButton>
      </FlexChild>
    </Form>
  );
};

export default SECExtraShortForm;
